import { Component, type JSX, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { PrimaryButton } from 'app/shared-components/buttons';
import ErrorState from 'app/shared-components/common/ErrorState';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Defaults to a full reload, which also refetches route chunks a deploy may have replaced. */
  reload?: () => void;
}

const reloadPage = (): void => window.location.reload();

// Route pages are lazy-loaded, so a failed chunk import or a render error would otherwise
// unmount the whole app. Keyed by route so navigating elsewhere leaves the error behind.
export default function RouteErrorBoundary({
  children,
  reload = reloadPage,
}: RouteErrorBoundaryProps): JSX.Element {
  const { pathname } = useLocation();

  return (
    <Boundary key={pathname} reload={reload}>
      {children}
    </Boundary>
  );
}

interface BoundaryProps {
  children: ReactNode;
  reload: () => void;
}

interface BoundaryState {
  hasError: boolean;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, reload } = this.props;

    if (!hasError) return children;

    return (
      <div className="vtwo:flex vtwo:grow vtwo:flex-col vtwo:items-center vtwo:justify-center vtwo:gap-4 vtwo:p-8">
        <ErrorState
          title="Something went wrong"
          subtitle="This page could not be displayed. Reload to try again."
        />
        <PrimaryButton label="Reload page" cb={reload} />
      </div>
    );
  }
}
